import { useState, useEffect, useRef } from 'react';
import { FEATURED_MARKET_CODES } from '../config/constants';
import { getCountry, formatUsd, resolveUserCountryCode, isSpecialCountryFilter } from '../data/countries';
import { getUserDisplayName, getUserInitials, getUserAvatarUrl } from '../utils/properties';
import apiClient from '../utils/api';
import { uploadProfilePhotoViaFirebase } from '../utils/submissions';
import PageRefinedHero from '../components/layout/PageRefinedHero';

const ProfilePage = ({
  user,
  balance,
  country,
  kycDone,
  isLoggedIn,
  onOpenKYC,
  onAddFunds,
  setPage,
  onProfileSaved,
  addToast
}) => {
  const profileCountry = () => {
    if (user?.country && !isSpecialCountryFilter(user.country)) {
      return resolveUserCountryCode(user.country);
    }
    return resolveUserCountryCode(country);
  };

  const [form, setForm] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    phone: user?.phone || "",
    country: profileCountry(),
  });
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(getUserAvatarUrl(user));
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [algorandWallet, setAlgorandWallet] = useState(null);
  const [copyingWalletAddress, setCopyingWalletAddress] = useState(false);
  const photoInputRef = useRef(null);
  const walletAddressRef = useRef(null);

  useEffect(() => {
    if (!isLoggedIn) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.getMyAlgorandWallet();
        if (!cancelled && res.data?.enabled && res.data?.address) {
          setAlgorandWallet(res.data);
        }
      } catch (_) {
        /* optional */
      }
    })();
    return () => { cancelled = true; };
  }, [isLoggedIn]);

  useEffect(() => {
    setForm({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      phone: user?.phone || "",
      country: user?.country && !isSpecialCountryFilter(user.country)
        ? resolveUserCountryCode(user.country)
        : resolveUserCountryCode(country),
    });
    setAvatarUrl(getUserAvatarUrl(user));
  }, [user?.firstName, user?.lastName, user?.phone, user?.country, user?.avatar, country]);

  const displayName = getUserDisplayName(user);
  const email = user?.email || "";
  const initials = getUserInitials(user);
  const kycStatus = user?.kycStatus || (kycDone ? "approved" : "pending");
  const kycLabel =
    kycStatus === "approved" ? "Verified" : kycStatus === "rejected" ? "Rejected" : "Pending review";
  const kycBadgeClass =
    kycStatus === "approved" ? "approved" : kycStatus === "rejected" ? "rejected" : "pending";

  const copyAlgorandAddress = async () => {
    const address = algorandWallet?.address;
    if (!address) return;
    setCopyingWalletAddress(true);
    try {
      await navigator.clipboard.writeText(address);
      addToast("Wallet address copied.", "success");
    } catch (_) {
      walletAddressRef.current?.focus();
      walletAddressRef.current?.select();
      addToast("Select the address and copy manually.", "info");
    } finally {
      setCopyingWalletAddress(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.updateProfile({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        country: form.country
      });
      if (onProfileSaved) await onProfileSaved();
      addToast("Profile updated.", "success");
    } catch (err) {
      addToast(err.message || "Could not save profile.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      addToast("Photo must be JPEG, PNG, or WebP.", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      addToast("Photo must be 5 MB or smaller.", "error");
      return;
    }

    setUploadingPhoto(true);
    try {
      let url = null;
      try {
        const res = await apiClient.uploadProfilePhoto(file);
        url = res.data?.url || res.data?.user?.avatar;
      } catch (apiErr) {
        const msg = String(apiErr.message || "");
        const storageUnavailable = /storage is not available|STORAGE_NOT_CONFIGURED|bucket does not exist/i.test(msg);
        if (!storageUnavailable) throw apiErr;
        url = await uploadProfilePhotoViaFirebase(file);
        await apiClient.updateProfile({ avatar: url });
      }
      if (url) setAvatarUrl(url);
      if (onProfileSaved) await onProfileSaved();
      addToast("Profile photo updated.", "success");
    } catch (err) {
      addToast(err.message || "Could not upload profile photo.", "error");
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="coown-page coown-page--hero">
        <PageRefinedHero
          badge="Your account"
          title="My"
          titleAccent="profile"
          subtitle="Sign in to manage identity verification, wallet balance, and your CoOwn account details."
          benefits={[
            { icon: 'fas fa-user-shield', title: 'Identity & KYC', subtitle: 'Verify to unlock investing' },
            { icon: 'fas fa-wallet', title: 'Wallet & funds', subtitle: 'Deposit and track balance' },
            { icon: 'fas fa-sliders-h', title: 'Preferences', subtitle: 'Name, phone, and market' },
          ]}
        />
        <div className="coown-page-body coown-page-body--profile">
          <div className="coown-page-empty">
            <i className="fas fa-user-circle coown-page-empty__icon" aria-hidden="true"></i>
            <h3>Sign in to view your profile</h3>
            <p>Manage your account, KYC status, and wallet after you log in.</p>
            <button type="button" className="btn btn-primary" onClick={() => setPage?.('login')}>Log in</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="coown-page coown-page--hero">
      <PageRefinedHero
        badge="Your account"
        title="My"
        titleAccent="profile"
        subtitle={`Welcome back, ${displayName.split(' ')[0] || 'investor'}. Manage verification, wallet, and account preferences in one place.`}
        benefits={[
          { icon: 'fas fa-user-shield', title: `KYC: ${kycLabel}`, subtitle: kycStatus === 'approved' ? 'Ready to invest' : 'Complete verification to invest' },
          { icon: 'fas fa-wallet', title: formatUsd(balance), subtitle: 'Available wallet balance' },
          { icon: 'fas fa-globe', title: getCountry(form.country).name, subtitle: 'Country of residence' },
        ]}
      />

      <div className="coown-page-body coown-page-body--profile">
      <div style={{ background: "var(--card)", borderRadius: "var(--radius)", padding: "1.75rem", boxShadow: "var(--shadow)", marginBottom: "1.5rem", textAlign: "center" }}>
        {avatarUrl ? (
          <button
            type="button"
            className="profile-avatar-button user-avatar"
            style={{ margin: "0 auto 1rem" }}
            onClick={() => setPhotoViewerOpen(true)}
            aria-label="View profile photo full size"
          >
            <img src={avatarUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </button>
        ) : (
          <div className="user-avatar" style={{ margin: "0 auto 1rem", overflow: "hidden" }}>
            {initials}
          </div>
        )}
        <input
          ref={photoInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: "none" }}
          onChange={handlePhotoSelect}
        />
        {avatarUrl ? (
          <p style={{ fontSize: "0.78rem", color: "var(--text-light)", margin: "-0.35rem 0 0.75rem" }}>
            Click your photo to view it full size.
          </p>
        ) : null}
        <button
          type="button"
          className="btn btn-outline btn-sm"
          style={{ marginBottom: "1rem" }}
          disabled={uploadingPhoto}
          onClick={() => photoInputRef.current?.click()}
        >
          <i className={`fas fa-${uploadingPhoto ? "spinner fa-spin" : "camera"}`}></i>
          {uploadingPhoto ? " Uploading…" : " Upload profile photo"}
        </button>
        <p style={{ fontSize: "0.8rem", color: "var(--text-light)", margin: "-0.5rem 0 1rem" }}>
          Use a clear photo of your face. Pets, logos, and other images are not accepted.
        </p>
        <h3 style={{ marginBottom: "0.25rem" }}>{displayName}</h3>
        <p style={{ color: "var(--text-light)", marginBottom: "1rem" }}>{email}</p>
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", flexWrap: "wrap", marginBottom: "1rem" }}>
          <span className={`status-badge status-${kycBadgeClass}`}>
            <i className="fas fa-user-shield" style={{ marginRight: "0.35rem" }}></i>
            KYC: {kycLabel}
          </span>
          <span style={{ fontSize: "0.9rem", padding: "0.35rem 0.75rem", background: "var(--bg)", borderRadius: 8, fontWeight: 600, color: "var(--primary)" }}>
            {formatUsd(balance)}
          </span>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", flexWrap: "wrap" }}>
          {kycStatus !== "approved" && (
            <button type="button" className="btn btn-primary btn-sm" onClick={onOpenKYC}>
              <i className="fas fa-id-card"></i> Verify identity
            </button>
          )}
          <button type="button" className="btn btn-outline btn-sm" onClick={onAddFunds}>
            <i className="fas fa-plus"></i> Add funds
          </button>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => setPage("dashboard")}>
            <i className="fas fa-chart-pie"></i> Portfolio
          </button>
        </div>
      </div>

      <div style={{ background: "var(--card)", borderRadius: "var(--radius)", padding: "1.75rem", boxShadow: "var(--shadow)" }}>
        <h3 style={{ marginBottom: "1rem", fontSize: "1.1rem" }}>Account details</h3>
        <form onSubmit={handleSave}>
          <div className="form-row">
            <div className="form-group">
              <label>First name</label>
              <input required value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Last name</label>
              <input required value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} />
            </div>
          </div>
          <div className="form-group">
            <label>Email</label>
            <input readOnly value={email} style={{ background: "var(--bg)", color: "var(--text-light)" }} />
            <p style={{ fontSize: "0.8rem", color: "var(--text-light)", marginTop: "0.35rem" }}>Email is managed through your sign-in provider.</p>
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input type="tel" placeholder="+1 555 000 0000" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Country of residence</label>
            <select value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}>
              {FEATURED_MARKET_CODES.map((code) => (
                <option key={code} value={code}>{getCountry(code).name} ({code})</option>
              ))}
            </select>
            <p style={{ fontSize: "0.8rem", color: "var(--text-light)", marginTop: "0.35rem" }}>
              Used for KYC labels and default market. Change the header country selector for browsing listings.
            </p>
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: "100%", padding: "0.9rem" }} disabled={saving}>
            <i className={`fas fa-${saving ? "spinner fa-spin" : "save"}`}></i> {saving ? "Saving…" : "Save changes"}
          </button>
        </form>
      </div>

      {algorandWallet?.address ? (
        <div className="profile-wallet-card">
          <h3 className="profile-wallet-card__title">Algorand wallet</h3>
          <p className="profile-wallet-card__desc">
            Your embedded wallet for tokenized property shares (ASA). Tokens are minted here when a funded property completes legal tokenization.
          </p>
          <div className="profile-wallet-card__row">
            <input
              ref={walletAddressRef}
              readOnly
              value={algorandWallet.address}
              onFocus={(e) => e.target.select()}
              onClick={(e) => e.target.select()}
              aria-label="Algorand wallet address"
              className="profile-wallet-card__address"
            />
            <button
              type="button"
              className="btn btn-outline btn-sm profile-wallet-card__copy"
              onClick={copyAlgorandAddress}
              disabled={copyingWalletAddress}
            >
              <i className={`fas fa-${copyingWalletAddress ? "spinner fa-spin" : "copy"}`} aria-hidden="true"></i>
              <span style={{ marginLeft: "0.35rem" }}>Copy</span>
            </button>
          </div>
          <p className="profile-wallet-card__network">
            Network: {algorandWallet.network || "testnet"}
          </p>
        </div>
      ) : null}

      <div style={{ marginTop: "1.25rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <button type="button" className="btn btn-outline" style={{ justifyContent: "flex-start" }} onClick={() => setPage("agent-dashboard")}>
          <i className="fas fa-clipboard-list" style={{ marginRight: "0.5rem" }}></i> My property listings
        </button>
        <button type="button" className="btn btn-outline" style={{ justifyContent: "flex-start" }} onClick={() => setPage("favorites")}>
          <i className="fas fa-heart" style={{ marginRight: "0.5rem" }}></i> Saved properties
        </button>
      </div>

      {photoViewerOpen && avatarUrl ? (
        <div className="modal-overlay active" onClick={() => setPhotoViewerOpen(false)}>
          <div className="profile-photo-viewer" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Profile photo">
            <button type="button" className="modal-close profile-photo-viewer__close" onClick={() => setPhotoViewerOpen(false)} aria-label="Close">&times;</button>
            <img src={avatarUrl} alt={`${displayName} profile photo`} className="profile-photo-viewer__image" />
            <div className="profile-photo-viewer__actions">
              <a href={avatarUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm">
                <i className="fas fa-external-link-alt" aria-hidden="true"></i> Open full size
              </a>
            </div>
          </div>
        </div>
      ) : null}
      </div>
    </div>
  );
};

export default ProfilePage;
